import { Trans, useTranslation } from "react-i18next";

import { Color, Icon, Link } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Semibold, Subheading } from "capitalrx-components-2.0/lib/typography";
import classNames from "classnames";

import { StackOrShelf } from "@/common/components/StackOrShelf";
import { useHasMedicarePlans } from "@/premium/hooks/useHasMedicarePlans";
import { useEnrollmentContext } from "@/state";
import { getBrandName } from "@/utils/branding";
import { enableComparePlansPage } from "@/utils/featureFlags";

import "./Footer.scss";
import { useSelectedEnrollmentPlan } from "@/premium/hooks";

const translationPath = "HEADER_AND_FOOTER";

export const Footer = () => {
  const experienceData = useEnrollmentContext();
  const urlLocation = window.location;
  const searchParams = new URLSearchParams(urlLocation.search);
  const clientName = searchParams.get("client");
  const menuItems = experienceData?.menu_items;
  const theme = experienceData?.theme;
  const hasMedicarePlans = useHasMedicarePlans();
  const { enrollmentSupportPhoneNumber, hidePlanCompare, memberSupportPhoneNumber } = useSelectedEnrollmentPlan();
  const medicareMaterialId = experienceData.theme.medicare_material_id;
  const { t: translate } = useTranslation();

  const { columns } = useResponsive({
    columns: {
      desktop: 4,
      tablet: 12,
      mobile: 12,
    },
  });

  return (
    <Stack className={classNames("footer-panel")} spacing="large">
      <Grid spacing="large">
        {enrollmentSupportPhoneNumber && (
          <Grid.Span columns={columns}>
            <Stack>
              <Shelf>
                <Icon name="phone" />
                <Subheading> {translate(`${translationPath}.enrollmentSupport`)}</Subheading>
                <Shelf.Spacer />
              </Shelf>
              <Body>
                <Trans
                  i18nKey={`${translationPath}.enrollmentSupportPhone`}
                  values={{
                    enrollmentNumber: enrollmentSupportPhoneNumber,
                  }}
                />
              </Body>
            </Stack>
          </Grid.Span>
        )}
        {memberSupportPhoneNumber && (
          <Grid.Span columns={columns}>
            <Stack>
              <Shelf>
                <Icon name="phone" />
                <Subheading> {translate(`${translationPath}.memberSupport`)}</Subheading>
                <Shelf.Spacer />
              </Shelf>
              <Body>
                <Trans
                  i18nKey={`${translationPath}.memberSupportPhone`}
                  values={{
                    memberNumber: memberSupportPhoneNumber,
                  }}
                />
              </Body>
            </Stack>
          </Grid.Span>
        )}
        <Grid.Span columns={columns}>
          <Stack>
            <Shelf>
              <Subheading> {translate(`${translationPath}.quickNav`)}</Subheading> <Shelf.Spacer />
            </Shelf>
            <Link className="footer-link " to={{ pathname: "/", search: `client=${clientName}` }}>
              {translate(`${translationPath}.home`)}
            </Link>
            <Link className="footer-link " to={{ pathname: "/plans", search: `client=${clientName}` }}>
              {translate(`${translationPath}.plans`)}
            </Link>
            {enableComparePlansPage() && !hidePlanCompare && (
              <Link className="footer-link " to={{ pathname: "/compare-plans", search: `client=${clientName}` }}>
                {translate(`${translationPath}.comparePlans`)}
              </Link>
            )}
            {menuItems
              .sort((a, b) => a.display_order - b.display_order)
              .map((item) => (
                <Shelf key={item.id}>
                  <Link key={item.id} className="footer-link " to={item.link_url}>
                    {item.link_name}
                  </Link>
                  <Icon color={Color.Turqoise["500"]} name="link-out" /> <Shelf.Spacer />
                </Shelf>
              ))}
          </Stack>
        </Grid.Span>
      </Grid>
      <StackOrShelf alignment="center">
        <img style={{ cursor: "pointer" }} data-testid="img-logo" height={30} src={theme?.logo_desktop} alt="Client Logo" />
        {hasMedicarePlans && medicareMaterialId && (
          <Body>
            <Semibold>{medicareMaterialId} </Semibold>
          </Body>
        )}
        <Body>
          {`© ${getBrandName()}, Inc.  -  `}
          <a href="https://www.cap-rx.com/legal#legal-notice-privacy-policy">{translate(`${translationPath}.legalNotices`)}</a>
        </Body>
        <Shelf.Spacer />
      </StackOrShelf>
    </Stack>
  );
};
